import React, { useState, useEffect } from "react";
import { API, Auth } from "aws-amplify";
import Header from "../../components/header/Header";
import profile from "../../assets/profile.png";
import { buildResumeURL } from "../../utils/resumeUrl";
import { RiseLoader } from "react-spinners";
import { 
  FiSearch, 
  FiUser, 
  FiMail, 
  FiPhone, 
  FiBriefcase,
  FiFileText,
  FiMapPin,
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiSend,
  FiEye,
  FiRefreshCw
} from "react-icons/fi";

const InterviewSlotBook = () => {
  const [candidateProfile, setCandidateProfile] = useState(null);
  const [interviewEvaluations, setInterviewEvaluations] = useState([]);
  const [allCandidates, setAllCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [email, setEmail] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [rejectEmail, setRejectEmail] = useState(false);
  const [sendUpdate, setSendUpdate] = useState(false);
  const [paginationToken, setPaginationToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);

  const BATCH_SIZE = 200;

  const override = {
    display: "block",
    margin: "0 auto",
    borderColor: "#3b82f6",
  };

  // Check authentication and fetch candidates on component mount
  useEffect(() => {
    const checkAuthAndFetch = async () => {
      try {
        await Auth.currentAuthenticatedUser();
        fetchAllCandidates();
      } catch (error) {
        console.error("Authentication error:", error);
        setErrorMessage("Please sign in to access admin features.");
        setIsLoadingAll(false);
      }
    };
    
    checkAuthAndFetch();
  }, []);

  // Local filter for quick filtering of already loaded candidates
  const filterLocalCandidates = (query) => {
    if (!query.trim()) {
      return allCandidates;
    }
    return allCandidates.filter(candidate => 
      candidate.fullName?.toLowerCase().includes(query.toLowerCase()) ||
      candidate.email?.toLowerCase().includes(query.toLowerCase()) ||
      candidate.mobileNumber?.includes(query) ||
      candidate.roleApplyFor?.toLowerCase().includes(query.toLowerCase())
    );
  };

  // Server-side search that paginates through the entire database
  const searchCandidatesServerSide = async (query) => {
    if (!query.trim()) {
      setFilteredCandidates(allCandidates);
      setSearchPerformed(false);
      return;
    }

    setIsSearching(true);
    setSearchPerformed(true);
    setErrorMessage("");

    try {
      let nextToken = null;
      let foundCandidates = [];
      let pagesSearched = 0;
      const MAX_PAGES = 50; // Safety limit to prevent infinite loops

      // Build filter for server-side search
      // GraphQL filter supports 'contains' for partial matches
      const searchFilter = {
        or: [
          { email: { contains: query.toLowerCase() } },
          { fullName: { contains: query } },
          { mobileNumber: { contains: query } },
          { roleApplyFor: { contains: query } }
        ]
      };

      do {
        let response;
        try {
          response = await API.graphql({
            query: `
              query ListCandidateProfiles($filter: ModelCandidateProfileFilterInput, $limit: Int, $nextToken: String) {
                listCandidateProfiles(filter: $filter, limit: $limit, nextToken: $nextToken) {
                  items {
                    id
                    fullName
                    email
                    mobileNumber
                    photoURL
                    currentCity
                    roleApplyFor
                    workStatus
                    description
                    keySkills
                    educationDetails
                    preferredSalary
                    workPreference
                    workMode
                    matchedPercentage
                    resumeURL
                    createdAt
                  }
                  nextToken
                }
              }
            `,
            variables: {
              filter: searchFilter,
              limit: BATCH_SIZE,
              nextToken: nextToken,
            }
          });
        } catch (partialError) {
          if (partialError.data && partialError.data.listCandidateProfiles) {
            response = partialError;
          } else {
            throw partialError;
          }
        }

        if (response.data && response.data.listCandidateProfiles) {
          const items = response.data.listCandidateProfiles.items || [];
          const validItems = items.filter(item => item && item.email);
          foundCandidates = [...foundCandidates, ...validItems];
          nextToken = response.data.listCandidateProfiles.nextToken;
        } else {
          break;
        }

        pagesSearched++;
        
        // If we found enough results, stop searching
        if (foundCandidates.length >= 100) {
          break;
        }
      } while (nextToken && pagesSearched < MAX_PAGES);

      // Sort by creation date (newest first)
      foundCandidates.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      
      setFilteredCandidates(foundCandidates);
      
      if (foundCandidates.length === 0) {
        setErrorMessage(`No candidates found matching "${query}"`);
      }
    } catch (error) {
      console.error("Error searching candidates:", error);
      // Fall back to local search
      const localResults = filterLocalCandidates(query);
      setFilteredCandidates(localResults);
      if (localResults.length === 0) {
        setErrorMessage(`Search failed. No candidates found matching "${query}" in loaded data.`);
      }
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search - triggers server search after user stops typing
  useEffect(() => {
    const query = searchQuery.trim();
    
    if (!query) {
      setFilteredCandidates(allCandidates);
      setSearchPerformed(false);
      return;
    }

    // First, immediately filter local candidates for quick feedback
    const localResults = filterLocalCandidates(query);
    setFilteredCandidates(localResults);

    // If local results are sufficient or no more data to search, don't search server
    if (localResults.length >= 10 || !hasMore) {
      return;
    }

    // Debounce server-side search
    const debounceTimer = setTimeout(() => {
      searchCandidatesServerSide(query);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(debounceTimer);
  }, [searchQuery, allCandidates, hasMore]);

  const fetchAllCandidates = async (loadMore = false) => {
    if (loadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoadingAll(true);
      setAllCandidates([]);
      setFilteredCandidates([]);
      setPaginationToken(null);
    }
    setErrorMessage("");
    
    try {
      let response;
      try {
        response = await API.graphql({
          query: `
            query ListCandidateProfiles($filter: ModelCandidateProfileFilterInput, $limit: Int, $nextToken: String) {
              listCandidateProfiles(filter: $filter, limit: $limit, nextToken: $nextToken) {
                items {
                  id
                  fullName
                  email
                  mobileNumber
                  photoURL
                  currentCity
                  roleApplyFor
                  workStatus
                  description
                  keySkills
                  educationDetails
                  preferredSalary
                  workPreference
                  workMode
                  matchedPercentage
                  resumeURL
                  createdAt
                }
                nextToken
              }
            }
          `,
          variables: {
            limit: BATCH_SIZE,
            nextToken: loadMore ? paginationToken : null,
          }
        });
      } catch (partialError) {
        // GraphQL may throw but still return partial data
        if (partialError.data && partialError.data.listCandidateProfiles) {
          response = partialError;
        } else {
          throw partialError;
        }
      }

      if (response.data && response.data.listCandidateProfiles) {
        const items = response.data.listCandidateProfiles.items || [];
        // Filter out any null items or items without email (invalid records)
        const validItems = items.filter(item => item && item.email);
        const newNextToken = response.data.listCandidateProfiles.nextToken;
        
        // Update pagination state
        setPaginationToken(newNextToken);
        setHasMore(!!newNextToken);
        
        // Update candidates list
        const updatedCandidates = loadMore 
          ? [...allCandidates, ...validItems]
          : validItems;
        
        // Sort by creation date (newest first)
        updatedCandidates.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        
        setAllCandidates(updatedCandidates);
        // Only update filtered if no search query
        if (!searchQuery.trim()) {
          setFilteredCandidates(updatedCandidates);
        }
      }
    } catch (error) {
      console.error("Error fetching candidates:", error);
      if (error.errors && error.errors.length > 0) {
        const errorMsg = error.errors[0].message || "Failed to load candidates";
        setErrorMessage(`${errorMsg}. Please check your authentication.`);
      } else {
        setErrorMessage("Failed to load candidates. Please check your authentication and try again.");
      }
    } finally {
      setIsLoadingAll(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !isLoadingMore) {
      fetchAllCandidates(true);
    }
  };

  const fetchCandidateDetails = async (candidateEmail, candidateFromTable = null) => {
    setIsLoading(true);
    setEmail(candidateEmail);
    setCandidateProfile(null);
    setInterviewEvaluations([]);
    setErrorMessage("");

    try {
      let candidateProfileItem = null;

      // First, check if we already have this candidate data from the table
      if (candidateFromTable && candidateFromTable.email === candidateEmail) {
        candidateProfileItem = candidateFromTable;
      } else {
        // Check in allCandidates and filteredCandidates
        const foundInAll = allCandidates.find(c => c.email === candidateEmail);
        const foundInFiltered = filteredCandidates.find(c => c.email === candidateEmail);
        candidateProfileItem = foundInAll || foundInFiltered;
      }

      // If not found locally, fetch from API
      if (!candidateProfileItem) {
        let candidateProfileResponse;
        try {
          candidateProfileResponse = await API.graphql({
            query: `
              query ListCandidateProfiles($filter: ModelCandidateProfileFilterInput, $limit: Int) {
                listCandidateProfiles(filter: $filter, limit: $limit) {
                  items {
                    id
                    fullName
                    email
                    gender
                    mobileNumber
                    photoURL
                    currentCity
                    roleApplyFor
                    workStatus
                    description
                    keySkills
                    educationDetails
                    preferredSalary
                    workPreference
                    workMode
                    workCategory
                    matchedPercentage
                    resumeURL
                    address
                    createdAt
                  }
                }
              }
            `,
            variables: {
              filter: { email: { eq: candidateEmail } },
              limit: 10,
            }
          });
        } catch (partialError) {
          if (partialError.data && partialError.data.listCandidateProfiles) {
            candidateProfileResponse = partialError;
          } else {
            throw partialError;
          }
        }

        if (candidateProfileResponse.data && candidateProfileResponse.data.listCandidateProfiles) {
          const items = candidateProfileResponse.data.listCandidateProfiles.items || [];
          const validItems = items.filter(item => item && item.email);
          if (validItems.length > 0) {
            candidateProfileItem = validItems[0];
          }
        }
      }

      if (!candidateProfileItem) {
        setErrorMessage("No candidate found with this email. Please verify the email address.");
        setIsLoading(false);
        return;
      }

      setCandidateProfile(candidateProfileItem);

      // Fetch interview evaluations
      let interviewEvaluationsItems = [];
      
      try {
        let interviewEvaluationsResponse;
        try {
          interviewEvaluationsResponse = await API.graphql({
            query: `
              query ListInterviewEvaluations($filter: ModelInterviewEvaluationFilterInput, $limit: Int) {
                listInterviewEvaluations(filter: $filter, limit: $limit) {
                  items {
                    id
                    candidateEmail
                    roundNumber
                    evaluation
                    feedbackForAI
                    transcript
                    videoURL
                    interviewSlotID
                    reappearRoundNumber
                    createdAt
                    updatedAt
                  }
                  nextToken
                }
              }
            `,
            variables: {
              filter: { candidateEmail: { eq: candidateEmail } },
              limit: 100,
            }
          });
        } catch (partialError) {
          if (partialError.data && partialError.data.listInterviewEvaluations) {
            interviewEvaluationsResponse = partialError;
          } else {
            throw partialError;
          }
        }
        
        if (interviewEvaluationsResponse.data && interviewEvaluationsResponse.data.listInterviewEvaluations) {
          const items = interviewEvaluationsResponse.data.listInterviewEvaluations.items || [];
          interviewEvaluationsItems = items.filter(item => item && item.id);
        }

        setInterviewEvaluations(interviewEvaluationsItems);
      } catch (evalError) {
        console.warn("Error fetching evaluations:", evalError);
        setInterviewEvaluations([]);
      }
    } catch (error) {
      console.error("Error fetching candidate data:", error);
      if (error.data) {
        // Partial data returned with errors - try to use it
        console.log("Partial data available, attempting to use it");
      }
      if (error.errors && error.errors.length > 0) {
        const errorMsg = error.errors[0].message || "Error loading candidate details";
        setErrorMessage(`${errorMsg}. Please check your permissions.`);
      } else {
        setErrorMessage("Error loading candidate details. Please check your authentication and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (email.trim()) {
      fetchCandidateDetails(email.trim());
    }
  };

  const handleShare = () => {
    if (email.trim() !== "") {
      const encodedEmail = encodeURIComponent(email);
      const shareableURL = `${window.location.origin}/ShowMySkills?email=${encodedEmail}`;
      navigator.clipboard.writeText(shareableURL);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 3000);
    }
  };

  const handleSendEmail = async (value) => {
    setLoading(true);
    setErrorMessage("");
    try {
      let emailContent = '';
      let candidateName = candidateProfile.fullName;
      let candidateEmail = candidateProfile.email;
      let subject = ``;

      if (value === 'rejection') {
        subject = `Update on Your Application with Varahi Technologies`;
        emailContent = `
        <html>
          <body>
            <p>Dear ${candidateName},</p>
            <p>Thank you for your interest in Varahi technologies and for taking the time to apply for various profiles with us.</p>
            <p>After careful consideration, we regret to inform you that we will not be moving forward with your application at this time. This decision was based on several factors, including the competitive nature of the candidate pool and specific requirements for the roles.</p>
            <p>We appreciate the effort you put into the application process and encourage you to consider enhancing your skills and experiences in the areas relevant to the roles you are interested in. We value your interest in Varahi technologies, and we believe that with further development, you could be a strong fit for future opportunities with us.</p>
            <p>You can rebook yourself on GetScreened and share your updated profile with our recruitment team. This will ensure that your enhanced qualifications are taken into account for future openings.</p>
            <p>Please feel free to reapply for any suitable positions that match your updated qualifications. We wish you the best in your career endeavors and hope to see your application again in the future.</p>
            <p>Thank you once again for your interest in Varahi technologies</p>
          </body>
        </html>
      `;
      } else if (value === 'selected') {
        subject = `Your GetScreened Interview Evaluation Results Are Available`;
        emailContent = `
        <html>
          <body>
            <p>Dear Candidate,</p>
            <p>We are pleased to announce that your GetScreened interview evaluation results are now available. Log in and access your scores and evaluation here: <a href="https://www.app.getscreened.in/">https://www.app.getscreened.in/</a></p>
            <p>For assistance, please refer to this video: <a href="https://youtu.be/YAobc3sGLM0">https://youtu.be/YAobc3sGLM0</a></p>
            <p>Showcase your skills and expertise by sharing your interview link with your professional network, including friends, colleagues, and recruiters.</p>
            <p>Your feedback is invaluable in helping us improve our platform. Share your thoughts with us: <a href="https://docs.google.com/forms/d/e/1FAIpQLScmRBD3N0Wblz9irjO5hDbSfS214vlrDbWIKJbOOG8Uc-acog/viewform?usp=sf_link">https://docs.google.com/forms/d/e/1FAIpQLScmRBD3N0Wblz9irjO5hDbSfS214vlrDbWIKJbOOG8Uc-acog/viewform?usp=sf_link</a></p>
            <p>Thank you for participating in the GetScreened interview process. We wish you the best of luck in your job search and professional endeavors.</p>
            <p>Best regards,</p>
            <p>GetScreened Team</p>
            <p><a href="https://www.app.getscreened.in/">GetScreened</a></p>
          </body>
        </html>
      `;
      }

      const myHeaders = new Headers();
      myHeaders.append("X-Api-Key", "abce-123");
      myHeaders.append("Content-Type", "application/json");

      const raw = JSON.stringify({
        subject: subject,
        body: emailContent,
        to: candidateEmail,
      });

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      const response = await fetch("https://pharmaclick.co.in/RazorPay/WeatherForecast/sendEmailSES", requestOptions);
      const result = await response.json();

      if (result.message === "Invalid API Key") {
        console.error("Invalid API Key");
        setErrorMessage("Failed to send email: Invalid API Key");
        setLoading(false);
        return false;
      }

      if (value === 'rejection') {
        setRejectEmail(true);
      } else if (value === 'selected') {
        setSendUpdate(true);
      }

      setTimeout(() => {
        setRejectEmail(false);
        setSendUpdate(false);
        setLoading(false);
      }, 3000);

    } catch (error) {
      console.error('Error sending email:', error);
      setErrorMessage('Failed to send email. Please try again later.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      <Header />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Page Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-medium text-white tracking-tight mb-2">
            Admin Dashboard
          </h1>
          <p className="text-slate-400 text-sm">
            Manage candidate profiles and interview evaluations
          </p>
          {localStorage.getItem("candidateProfileEmailID") && (
            <p className="text-slate-500 text-xs mt-2">
              Logged in as: {localStorage.getItem("candidateProfileEmailID")}
            </p>
          )}
        </div>

        {/* Search Section */}
        <div className="bg-slate-800/40 rounded-xl p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-slate-400 text-sm mb-2">Search by Email</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Enter candidate email..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1 bg-slate-700 text-white border border-slate-600 rounded-lg py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSearch}
                  disabled={!email.trim() || isLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white px-6 py-2.5 rounded-lg transition-all flex items-center gap-2 font-medium"
                >
                  <FiSearch size={18} />
                  Search
                </button>
              </div>
            </div>
            <div className="flex-1">
              <label className="block text-slate-400 text-sm mb-2">
                Search Candidates
                {isSearching && (
                  <span className="ml-2 text-blue-400">
                    <FiRefreshCw className="inline animate-spin" size={12} /> Searching database...
                  </span>
                )}
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by name, email, phone, or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-slate-700 text-white border border-slate-600 rounded-lg py-2.5 px-4 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    <FiXCircle size={18} />
                  </button>
                )}
              </div>
              {searchQuery.trim() && (
                <p className="text-slate-500 text-xs mt-1">
                  {isSearching 
                    ? "🔍 Searching entire database..." 
                    : searchPerformed 
                      ? `Found ${filteredCandidates.length} candidate(s) in database`
                      : hasMore 
                        ? `Found ${filteredCandidates.length} in loaded data. Will search database if needed...`
                        : `Found ${filteredCandidates.length} candidate(s)`
                  }
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-500/10 border border-red-500/50 text-red-400 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <FiXCircle size={20} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium mb-1">Error</p>
                <p className="text-sm">{errorMessage}</p>
                {errorMessage.includes("permission") || errorMessage.includes("authentication") ? (
                  <p className="text-xs mt-2 text-red-300">
                    Tip: Make sure you're logged in with an admin account (getscreened@varahitechnologies.com or hr@varahitechnologies.com)
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Success Messages */}
        {rejectEmail && (
          <div className="bg-yellow-500/10 border border-yellow-500/50 text-yellow-400 rounded-lg p-4 mb-6 flex items-center gap-2">
            <FiCheckCircle />
            Rejection email sent successfully!
          </div>
        )}
        {sendUpdate && (
          <div className="bg-green-500/10 border border-green-500/50 text-green-400 rounded-lg p-4 mb-6 flex items-center gap-2">
            <FiCheckCircle />
            Update email sent successfully!
          </div>
        )}

        {/* Loading State for Candidate Details */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <RiseLoader color={"#3b82f6"} loading={isLoading} css={override} size={15} />
          </div>
        )}

        {/* Candidate Details View */}
        {!isLoading && candidateProfile && (
          <div className="space-y-6 mb-8">
            {/* Profile Card */}
            <div className="bg-slate-800/40 rounded-xl p-6">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-shrink-0">
                  <img 
                    src={candidateProfile.photoURL || profile} 
                    alt="Candidate" 
                    className="w-32 h-32 rounded-full object-cover border-4 border-slate-700"
                  />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <h2 className="text-2xl font-semibold text-white mb-1">
                      {candidateProfile.fullName}
                    </h2>
                    <p className="text-slate-400 text-sm">{candidateProfile.roleApplyFor || 'No role specified'}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2 text-slate-300">
                      <FiMail className="text-blue-400" />
                      <span className="text-sm">{candidateProfile.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <FiPhone className="text-blue-400" />
                      <span className="text-sm">{candidateProfile.mobileNumber || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <FiMapPin className="text-blue-400" />
                      <span className="text-sm">{candidateProfile.currentCity || 'N/A'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <FiBriefcase className="text-blue-400" />
                      <span className="text-sm">{candidateProfile.workStatus || 'N/A'}</span>
                    </div>
                  </div>

                  {candidateProfile.description && (
                    <div className="pt-2">
                      <p className="text-slate-400 text-sm leading-relaxed">{candidateProfile.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Details */}
              <div className="mt-6 pt-6 border-t border-slate-700 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {candidateProfile.keySkills && (
                  <div>
                    <label className="text-slate-500 text-xs uppercase mb-1 block">Key Skills</label>
                    <p className="text-slate-300 text-sm">{candidateProfile.keySkills}</p>
                  </div>
                )}
                {candidateProfile.educationDetails && (
                  <div>
                    <label className="text-slate-500 text-xs uppercase mb-1 block">Education</label>
                    <p className="text-slate-300 text-sm">{candidateProfile.educationDetails}</p>
                  </div>
                )}
                {candidateProfile.preferredSalary && (
                  <div>
                    <label className="text-slate-500 text-xs uppercase mb-1 block">Expected Salary</label>
                    <p className="text-slate-300 text-sm flex items-center gap-1">
                      <FiDollarSign size={14} />
                      {candidateProfile.preferredSalary}
                    </p>
                  </div>
                )}
                {candidateProfile.workPreference && (
                  <div>
                    <label className="text-slate-500 text-xs uppercase mb-1 block">Work Preference</label>
                    <p className="text-slate-300 text-sm">{candidateProfile.workPreference}</p>
                  </div>
                )}
                {candidateProfile.workMode && (
                  <div>
                    <label className="text-slate-500 text-xs uppercase mb-1 block">Work Mode</label>
                    <p className="text-slate-300 text-sm">{candidateProfile.workMode}</p>
                  </div>
                )}
                {candidateProfile.matchedPercentage && (
                  <div>
                    <label className="text-slate-500 text-xs uppercase mb-1 block">Match Percentage</label>
                    <p className="text-slate-300 text-sm">{candidateProfile.matchedPercentage}%</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={handleShare}
                  className="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all text-sm font-medium"
                >
                  {copied ? 'Link Copied!' : 'Share Profile'}
                </button>
                <button
                  onClick={() => handleSendEmail('selected')}
                  disabled={loading}
                  className="bg-green-600 hover:bg-green-700 disabled:bg-slate-700 text-white px-4 py-2 rounded-lg transition-all text-sm font-medium flex items-center gap-2"
                >
                  <FiSend size={16} />
                  {loading ? 'Sending...' : 'Send Selection Email'}
                </button>
                <button
                  onClick={() => handleSendEmail('rejection')}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-slate-700 text-white px-4 py-2 rounded-lg transition-all text-sm font-medium flex items-center gap-2"
                >
                  <FiSend size={16} />
                  {loading ? 'Sending...' : 'Send Rejection Email'}
                </button>
                {buildResumeURL(candidateProfile.resumeURL) && (
                  <a
                    href={buildResumeURL(candidateProfile.resumeURL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all text-sm font-medium flex items-center gap-2"
                  >
                    <FiFileText size={16} />
                    View Resume
                  </a>
                )}
              </div>
            </div>

            {/* Interview Evaluations */}
            {interviewEvaluations.length > 0 && (
              <div className="bg-slate-800/40 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                  <FiCheckCircle className="text-green-400" />
                  Interview Evaluations ({interviewEvaluations.length})
                </h3>
                <div className="space-y-4">
                  {interviewEvaluations.map((evaluation, index) => {
                    let parsedEvaluation = null;
                    let totalScore = 0;
                    let totalMaxScore = 0;
                    let overallRating = "N/A";

                    try {
                      if (evaluation.evaluation) {
                        parsedEvaluation = JSON.parse(evaluation.evaluation);
                        totalScore = Object.values(parsedEvaluation).reduce((sum, data) => sum + (data.score || 0), 0);
                        totalMaxScore = Object.values(parsedEvaluation).reduce((sum, data) => sum + (data.max_score || 0), 0);
                        overallRating = totalMaxScore ? ((totalScore / totalMaxScore) * 10).toFixed(2) : "N/A";
                      }
                    } catch (e) {
                      console.error("Error parsing evaluation:", e);
                    }

                    return (
                      <div key={evaluation.id} className="bg-slate-700/40 rounded-lg p-4 border border-slate-600">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <span className="text-blue-400 font-medium">Round {evaluation.roundNumber || index + 1}</span>
                            <p className="text-slate-400 text-xs mt-1">
                              <FiClock className="inline mr-1" size={12} />
                              {new Date(evaluation.createdAt).toLocaleString()}
                            </p>
                            {overallRating !== "N/A" && (
                              <p className="text-green-400 text-sm font-semibold mt-1">
                                Overall Rating: {overallRating}/10
                              </p>
                            )}
                          </div>
                          {evaluation.videoURL && (
                            <a
                              href={evaluation.videoURL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                            >
                              <FiEye size={14} />
                              View Recording
                            </a>
                          )}
                        </div>
                        
                        {parsedEvaluation && (
                          <div className="space-y-2">
                            {Object.entries(parsedEvaluation).map(([criterion, data], idx) => (
                              <div key={idx} className="bg-slate-800/60 rounded p-3">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-slate-300 text-sm font-medium">{criterion}</span>
                                  <span className="text-blue-400 font-semibold">
                                    {data.score || 'N/A'} / {data.max_score || 'N/A'}
                                  </span>
                                </div>
                                {data.comments && (
                                  <p className="text-slate-400 text-xs mt-1">{data.comments}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {evaluation.feedbackForAI && (
                          <div className="mt-3 pt-3 border-t border-slate-600">
                            <p className="text-slate-400 text-sm">
                              <span className="text-slate-300 font-medium">Feedback: </span>
                              {evaluation.feedbackForAI}
                            </p>
                          </div>
                        )}

                        {evaluation.transcript && (
                          <div className="mt-3 pt-3 border-t border-slate-600">
                            <details className="text-slate-400 text-sm">
                              <summary className="text-slate-300 font-medium cursor-pointer">View Transcript</summary>
                              <p className="mt-2 text-xs">{evaluation.transcript}</p>
                            </details>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* All Candidates List */}
        <div className="bg-slate-800/40 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <FiUser />
              {searchQuery.trim() 
                ? `Search Results (${filteredCandidates.length})`
                : `All Candidates (${filteredCandidates.length}${hasMore ? '+' : ''})`
              }
            </h3>
            <div className="flex items-center gap-3">
              {searchQuery.trim() && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSearchPerformed(false);
                    setFilteredCandidates(allCandidates);
                  }}
                  className="text-slate-400 hover:text-white text-sm flex items-center gap-1"
                >
                  <FiXCircle size={14} />
                  Clear Search
                </button>
              )}
              <button
                onClick={() => fetchAllCandidates()}
                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                disabled={isLoadingAll || isSearching}
              >
                <FiRefreshCw size={14} className={isLoadingAll ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {isLoadingAll ? (
            <div className="flex justify-center items-center py-12">
              <RiseLoader color={"#3b82f6"} loading={isLoadingAll} css={override} size={15} />
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FiUser className="text-4xl mx-auto mb-3 opacity-20" />
              <p>No candidates found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50 text-slate-300 text-xs uppercase">
                  <tr>
                    <th className="px-6 py-3 text-left">Name</th>
                    <th className="px-6 py-3 text-left">Email</th>
                    <th className="px-6 py-3 text-left">Phone</th>
                    <th className="px-6 py-3 text-left">Role</th>
                    <th className="px-6 py-3 text-left">Location</th>
                    <th className="px-6 py-3 text-left">Created</th>
                    <th className="px-6 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredCandidates.map((candidate) => (
                    <tr 
                      key={candidate.id} 
                      className="hover:bg-slate-700/30 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={candidate.photoURL || profile} 
                            alt="" 
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <span className="text-slate-200 text-sm font-medium">{candidate.fullName || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-400 text-sm">{candidate.email}</td>
                      <td className="px-6 py-4 text-slate-400 text-sm">{candidate.mobileNumber || 'N/A'}</td>
                      <td className="px-6 py-4 text-slate-400 text-sm">{candidate.roleApplyFor || 'N/A'}</td>
                      <td className="px-6 py-4 text-slate-400 text-sm">{candidate.currentCity || 'N/A'}</td>
                      <td className="px-6 py-4 text-slate-400 text-sm">
                        {candidate.createdAt ? new Date(candidate.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => fetchCandidateDetails(candidate.email, candidate)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
            
          {/* Load More Button */}
          {hasMore && !searchQuery.trim() && !isLoadingAll && (
            <div className="mt-6 text-center">
              <button
                onClick={handleLoadMore}
                disabled={isLoadingMore}
                className="bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all inline-flex items-center gap-2"
              >
                {isLoadingMore ? (
                  <>
                    <FiRefreshCw className="animate-spin" size={16} />
                    Loading...
                  </>
                ) : (
                  <>
                    Load More Candidates
                  </>
                )}
              </button>
              <p className="text-slate-500 text-xs mt-2">
                Showing {allCandidates.length} candidates
              </p>
            </div>
          )}
          
          {/* Total count when all loaded */}
          {!hasMore && allCandidates.length > 0 && !searchQuery.trim() && !isLoadingAll && (
            <p className="text-slate-500 text-xs text-center mt-4">
              Showing all {allCandidates.length} candidates
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default InterviewSlotBook;
